import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { BarChart3 } from "lucide-react";
import { usePayrollUIConfig } from "../../hooks/usePayrollUIConfig";

const COLORS = ["bg-primary/30", "bg-primary/45", "bg-primary/60", "bg-primary/80", "bg-primary"];

const SalaryDistributionChart = ({ entries }) => {
  const { t } = useTranslation();
  const { config } = usePayrollUIConfig();
  const currency = config?.currency || '';
  const configuredRanges = config?.salaryRanges || [
    { label: "< 1000", min: 0, max: 1000 },
    { label: "1000-2000", min: 1000, max: 2000 },
    { label: "2000-3000", min: 2000, max: 3000 },
    { label: "3000-4000", min: 3000, max: 4000 },
    { label: "> 4000", min: 4000, max: Infinity },
  ];

  const salaryRanges = useMemo(() => {
    if (!entries.length) return [];

    const ranges = configuredRanges.map((r, i) => ({
      ...r,
      count: 0,
      color: COLORS[i % COLORS.length],
    }));

    entries.forEach((entry) => {
      const salary = entry.net_pay || 0;
      const range = ranges.find((r) => salary >= r.min && salary < r.max);
      if (range) range.count++;
    });

    const maxCount = Math.max(...ranges.map((r) => r.count));
    return ranges.map((r) => ({ ...r, percentage: maxCount ? (r.count / maxCount) * 100 : 0 }));
  }, [entries, configuredRanges]);

  return (
    <Card className="overflow-hidden">
      <CardHeader style={{ backgroundColor: "var(--lm-s2)", borderBottom: "1px solid var(--lm-border)" }}>
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(94,106,210,0.10)" }}>
            <BarChart3 className="h-4 w-4" style={{ color: "#5e6ad2" }} />
          </div>
          <div>
            <span>{t('payrollRep.salaryDist')}</span>
            <p className="text-xs font-normal mt-0.5" style={{ color: "var(--lm-ink-3)" }}>{t('payrollRep.salaryDistSubtitle')}</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          {salaryRanges.map((range, index) => (
            <div key={range.label} className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="font-medium text-muted-foreground">{range.label}{currency ? ` ${currency}` : ''}</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-foreground">{range.count}</span>
                  <span className="text-muted-foreground">{t('payrollRep.employees')}</span>
                </div>
              </div>
              <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${range.color} rounded-full transition-all duration-700`}
                  style={{ width: `${range.percentage}%`, animationDelay: `${index * 0.1}s` }}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SalaryDistributionChart;
